import { onLoaded, loadContent, initializeInitialState } from "@/utils";
import { showReloadModal } from "@/modal";
import { isLicenseActive } from "./license";

// Prevent multiple initializations
let navigationInitialized = false

const Navigation = () => {
    // Prevent re-initialization
    if (navigationInitialized) {
        return
    }

    const settings = ajaxpress_vars.settings || {}
    const isLicenseValid = isLicenseActive()

    onLoaded( () => {
        // Prevent re-initialization
        if (navigationInitialized) {
            return
        }

        // Check if should be disabled.
        if (shouldDisable()) {
            return
        }

        // Mark as initialized
        navigationInitialized = true

        // Initialize the initial state for the current page
        initializeInitialState();

        // Listen for clicks on links (bubble phase to allow frameworks to handle first)
        document.addEventListener('click', clicked.bind(this), false);

        // Popstate event.
        window.addEventListener('popstate', statePopped.bind(this))

        // Prevent accidental reloads (Premium feature).
        if (settings.prevent_reloads && isLicenseValid) {
            preventReloads()
        }
    })

    const shouldDisable = () => {
        // Premium features require valid license
        if (settings.disable_for_mobile && isLicenseValid && isMobile()) {
            return true
        }

        if (settings.disable_logged_in_users && isLicenseValid && ajaxpress_vars.user_logged_in) {
            return true
        }

        return false
    }

    const isMobile = () => {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    }

    const preventReloads = () => {
        let isReloading = false
        let pendingReload = false
        
        window.addEventListener('keydown', async (e) => {
            // Handle Ctrl+R / Cmd+R
            if ((e.ctrlKey || e.metaKey) && (e.key === 'r' || e.key === 'R')) {
                e.preventDefault()
                e.stopPropagation()
                e.stopImmediatePropagation()
                
                const shouldReload = await showReloadModal()
                if (shouldReload) {
                    isReloading = true
                    window.location.reload()
                }
            }
            
            // Handle F5
            if (e.key === 'F5') {
                e.preventDefault()
                e.stopPropagation()
                e.stopImmediatePropagation()
                
                const shouldReload = await showReloadModal()
                if (shouldReload) {
                    isReloading = true
                    window.location.reload()
                }
            }
        }, true) // Use capture phase to catch early
        
        // Handle browser reload button
        window.addEventListener('beforeunload', (e) => {
            if (!isReloading && !pendingReload) {
                // Prevent the reload
                e.preventDefault()
                e.returnValue = ''
                
                // Set flag to prevent multiple triggers
                pendingReload = true
                
                // Show our custom modal after preventing the reload
                // Use setTimeout to allow the modal to show (beforeunload is synchronous)
                setTimeout(async () => {
                    const shouldReload = await showReloadModal()
                    if (shouldReload) {
                        isReloading = true
                        pendingReload = false
                        window.location.reload()
                    } else {
                        pendingReload = false
                    }
                }, 0)
                
                return e.returnValue
            }
        })
    }

    const isElementIgnored = (element) => {
        // Premium feature: ignore_elements requires valid license
        if (!isLicenseValid) {
            return false
        }

        // Check if element or any of its parents match ignore_elements selectors
        if (!element || !settings.ignore_elements) return false

        const selectors = settings.ignore_elements.split(',').map(s => s.trim()).filter(s => s.length > 0)
        if (selectors.length === 0) return false

        // Check if element or any of its parents match any of the selectors
        // closest() checks the element itself and all its ancestors
        for (let i = 0; i < selectors.length; i++) {
            try {
                if (element.closest && element.closest(selectors[i])) {
                    return true
                }
            } catch (err) {
                // Invalid selector, skip it
                console.warn('AjaxPress: Invalid selector in ignore_elements:', selectors[i])
            }
        }
        return false
    }

    const shouldIgnore = (e) => {
        // If has .ajaxpress-ignore class.
        if ( e.target.classList && e.target.classList.contains('ajaxpress-ignore') ) return true

        // Get the actual element that was clicked (might be a child of the link)
        let clickedElement = e.target
        // Find the anchor element first
        let anchorElement = null
        if (clickedElement.tagName === 'A') {
            anchorElement = clickedElement
        } else if (clickedElement.closest) {
            anchorElement = clickedElement.closest('a')
        }

        // Skip links that start with '#' - check href attribute before URL resolution.
        if ( anchorElement ) {
            const href = anchorElement.getAttribute('href')
            // Skip if href starts with '#'
            if ( href && href.trim().startsWith('#') ) {
                return true
            }
        }

        // If clicked element is not a link, find the nearest link ancestor
        if (clickedElement.tagName !== 'A' && clickedElement.closest) {
            const nearestLink = clickedElement.closest('a')
            if (nearestLink) {
                clickedElement = nearestLink
            }
        }

        // Check if clicked element or any of its parents match ignore_elements
        if (clickedElement && isElementIgnored(clickedElement)) return true

        const url = clickedElement?.href || e.target?.href || e.state?.url || null

        // Invalid href.
        if ( ! url ) return true

        // Ignore email, tels, etc.
        const ignore_schemes = ['#', 'data:', 'mailto:', 'javascript:', 'ftp:', 'file:', 'tel:', 'sms:', 'geo:', 'ws:']
        for( let index = 0; index < ignore_schemes.length; index++ ) {
            if ( url.startsWith(ignore_schemes[index]) ) return true
        }

        // Ignore admin routes.
        const admin_routes = ['wp-admin', 'wp-login', 'wp-content', 'wp-json']
        for( let index = 0; index < admin_routes.length; index++ ) {
            if ( url.startsWith( ajaxpress_vars.site.url + '/' + admin_routes[index] ) ) return true
        }

         // If External URL.
         if ( url.startsWith('http') || url.startsWith('//') ) {
            if ( ! url.startsWith(ajaxpress_vars.site.url) ) return true
        }

        // Ignore excluded routes (Premium feature).
        if ( settings.ignore_links && isLicenseValid ) {
            const ignorableLinks = settings.ignore_links.split('\n')

            for( let index = 0; index < ignorableLinks.length; index++ ) {
                const ignorableLink = new RegExp(ajaxpress_vars.site.url + '/' + ignorableLinks[index])
                if ( ignorableLink.test(url) ) return true
            }
        }

        return false
    }

    const clicked = async (e) => {
        // If another handler already prevented default (e.g., Vue/React router), respect it
        if (e.defaultPrevented) {
            return
        }

        // Only process clicks on anchor tags or elements inside anchor tags
        let anchorElement = null
        if (e.target.tagName === 'A') {
            anchorElement = e.target
        } else if (e.target.closest) {
            anchorElement = e.target.closest('a')
        }

        // If no anchor element found, ignore this click (not a link)
        if (!anchorElement) {
            return
        }

        // Early check for hash links - let browser/frameworks handle them
        const href = anchorElement.getAttribute('href')
        if (href && href.trim().startsWith('#')) {
            // Don't prevent default - let browser scroll or framework route
            return
        }
        
        // Disable while loading.
        if ( window.ajaxpress_state.loading && settings.animate_cursor && settings.disable_mouse_clicks ) {
            e.preventDefault()
            e.stopPropagation()
            return
        }

        // Check if the hyperlink should be ignored
        const shouldIgnoreResult = shouldIgnore( e )
        if ( shouldIgnoreResult ) {
            // Don't prevent default - let the link work normally
            return
        }

        // Get the actual link element (might be a child of the link)
        let linkElement = e.target
        if (linkElement.tagName !== 'A' && linkElement.closest) {
            const nearestLink = linkElement.closest('a')
            if (nearestLink) {
                linkElement = nearestLink
            }
        }

        // Only prevent default if we're actually processing this link via AJAX
        e.preventDefault();
        e.stopPropagation();

        // Fetch URL.
        await loadContent(linkElement.href, 'click')
    }

    const statePopped = (e) => {
        // Handle cases where e.state might be null or undefined
        // This can happen when navigating to states that weren't pushed by the application
        // or when the initial page load doesn't have a state object
        const url = (e.state && e.state.url) ? e.state.url : window.location.href
        
        // Create a mock event object for shouldIgnore check since e.target might not exist
        const mockEvent = {
            target: { href: url },
            state: e.state
        }
        
        if ( shouldIgnore( mockEvent ) ) return;

        loadContent(url, 'popstate')
    }


}

export default Navigation